import React, { useState, useCallback, useEffect } from 'react';
import _ from 'lodash';
import { Button } from '@this/shared/ui/inputs/button';
import moment from 'moment';
import type { Moment } from 'moment/moment';
import useApi from '@this/components/shared/hooks/use_api';
import { Fetcher } from '@this/src/util';

import type { AllowanceArgs } from '@this/domain/allowance/allowance';
import { Allowance } from '@this/domain/allowance/allowance';
import type { AllowanceTargetType } from '@this/domain/organization/organization2';

import { Loading } from '@this/shared/ui/feedbacks/loading';
import DatetimePicker from '@this/shared/datetime_picker/datetime_picker';

import { OrganizationTitle, OrganizationBody, DownloadButton } from '../organization.style';

interface AllowanceResponse {
  allowances: AllowanceArgs[];
}

interface ProjectsResponse {
  projects: Array<{
    id: number;
    name: string;
    code: string;
    allowance_target: boolean;
  }>;
}

interface OrganizationResponse {
  organization: {
    allowance_target_type?: AllowanceTargetType;
  };
}

const Allowances = () => {
  const getUrlParam = (key: string): string | null => {
    const params = new URLSearchParams(window.location.search);
    return params.get(key);
  };

  const [from, setFrom] = useState<Moment>(() => {
    const fromParam = getUrlParam('from');
    return fromParam ? moment(fromParam) : moment().startOf('month');
  });
  const [to, setTo] = useState<Moment>(() => {
    const toParam = getUrlParam('to');
    return toParam ? moment(toParam) : moment().endOf('month');
  });

  const [aggregateTarget, setAggregateTarget] = useState(() => {
    return getUrlParam('aggregateTarget') || 'contain_start_or_end';
  });
  const [downloading, setDownloading] = useState(false);
  const [csvEncoding, setCsvEncoding] = useState('utf8');
  const [csvTarget, setCsvTarget] = useState('person');
  const [projectFilter, setProjectFilter] = useState(() => {
    return getUrlParam('projectFilter') || 'all';
  });
  const [organizationTripType, setOrganizationTripType] = useState<AllowanceTargetType>('all');
  const [projectAllowanceInfo, setProjectAllowanceInfo] = useState<{ total: number; target: number }>({
    total: 0,
    target: 0
  });
  const params = {
    from: from.format('YYYY-MM-DD'),
    to: to.format('YYYY-MM-DD'),
    aggregateTarget,
    projectFilter
  };
  const allowanceResponse = useApi<AllowanceResponse>('/organization/allowances.json', params);
  const allowanceRawData = allowanceResponse.data;
  const isAllowanceLoading = allowanceResponse.isLoading;
  const allowanceData = allowanceRawData
    ? allowanceRawData.allowances.map((args: AllowanceArgs) => new Allowance(args))
    : [];

  // プロジェクトの日当対象設定とOrganization設定を取得
  useEffect(() => {
    // プロジェクト情報を取得
    Fetcher.get<ProjectsResponse>('/organization/projects.json')
      .then(res => {
        const total = res.projects.length;
        const target = res.projects.filter(p => p.allowance_target).length;
        setProjectAllowanceInfo({ total, target });
      })
      .catch(err => console.error('Failed to fetch projects:', err));

    // Organization設定を取得
    Fetcher.get<OrganizationResponse>('/organization/setting.json')
      .then(res => {
        if (res.organization?.allowance_target_type) {
          setOrganizationTripType(res.organization.allowance_target_type);
        }
      })
      .catch(err => console.error('Failed to fetch organization settings:', err));
  }, []);

  const handleCSVDownload = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setDownloading(true);
    new Promise<void>(resolve => {
      const csvParams = new URLSearchParams({
        from: from.format('YYYY-MM-DD'),
        to: to.format('YYYY-MM-DD'),
        encoding: csvEncoding,
        csvTarget,
        aggregateTarget,
        projectFilter
      });
      location.href = `/organization/allowances.csv?${csvParams.toString()}`;
      resolve();
    }).then(() => {
      setTimeout(() => {
        setDownloading(false);
      }, 5000);
    });
  };

  const changeDate = useCallback(() => {
    setFrom(from);
    setTo(to);
    setAggregateTarget(aggregateTarget);
    setProjectFilter(projectFilter);
  }, [from, to, aggregateTarget, projectFilter]);

  return (
    <div>
      <OrganizationTitle>日当一覧</OrganizationTitle>
      <OrganizationBody>
        {isAllowanceLoading ? (
          <Loading />
        ) : (
          <>
            <div style={getStyles().dateGroup}>
              <p style={getStyles().labelText}>期間：</p>
              <DatetimePicker
                value={from}
                onChange={d => setFrom(d)}
                onClear={() => setFrom(moment().startOf('month'))}
                showTime={false}
                disabledDays={0}
                showPast
                border
              />
              <span style={getStyles().labelText}>〜</span>
              <DatetimePicker
                value={to}
                onChange={d => setTo(d)}
                onClear={() => setTo(moment().endOf('month'))}
                showTime={false}
                disabledDays={0}
                showPast
                border
              />

              <p style={getStyles().label}>絞り込み：</p>
              <select
                style={getStyles().select}
                value={aggregateTarget}
                onChange={e => setAggregateTarget(e.target.value)}
              >
                <option value="contain_start_or_end">出張期間が含まれている旅程</option>
                <option value="contain_start_date">出発日が含まれている旅程</option>
                <option value="contain_end_date">帰着日が含まれている旅程</option>
              </select>
              <select
                style={getStyles().select}
                value={projectFilter}
                onChange={e => setProjectFilter(e.target.value)}
              >
                <option value="all">指定なし</option>
                <option value="allowance_only">日当対象のプロジェクトのみ</option>
              </select>
              <Button style={getStyles().button} onClick={() => changeDate()}>
                更新
              </Button>
            </div>
            <div style={getStyles().dateGroup}>
              <select style={getStyles().select} value={csvTarget} onChange={e => setCsvTarget(e.target.value)}>
                <option value="person">人別集計データ</option>
                <option value="daily">日別集計データ</option>
              </select>
              <select
                style={getStyles().select}
                value={csvEncoding}
                onChange={e => setCsvEncoding(e.target.value)}
              >
                <option value="utf8">Macなど（文字コード：UTF8）</option>
                <option value="sjis">Windows（文字コード：SJIS）</option>
              </select>
              <DownloadButton type="button" disabled={downloading} onClick={handleCSVDownload}>
                CSVをダウンロード
              </DownloadButton>
            </div>

            <div style={getStyles().searchSection}>
              <div style={getStyles().searchItem}>
                <p style={getStyles().label}>
                  対象プロジェクト: {projectAllowanceInfo.target}件 / {projectAllowanceInfo.total}件
                </p>
                <p style={getStyles().label}>
                  対象旅程種別: {organizationTripType === 'hotel_only' ? '宿泊施設を含む旅程のみ' : 'すべての旅程'}
                </p>
              </div>
            </div>
            <table style={getStyles().table}>
              <thead>
                <tr>
                  <th style={getStyles().th}>出張者</th>
                  <th style={getStyles().th}>社員番号</th>
                  <th style={getStyles().th}>国内日帰り日当数</th>
                  <th style={getStyles().th}>国内宿泊日当数</th>
                  <th style={getStyles().th}>海外宿泊日当数</th>
                  <th style={getStyles().th}>国内日帰り日当単価</th>
                  <th style={getStyles().th}>国内宿泊日当単価</th>
                  <th style={getStyles().th}>海外宿泊日当単価</th>
                  <th style={getStyles().th}>国内日帰り日当合計</th>
                  <th style={getStyles().th}>国内宿泊日当合計</th>
                  <th style={getStyles().th}>海外宿泊日当合計</th>
                  <th style={getStyles().th}>日当合計</th>
                </tr>
              </thead>
              <tbody>
                {allowanceData.map((item, i) => (
                  <tr key={i}>
                    <td style={getStyles().td}>{item.name}</td>
                    <td style={getStyles().td}>{item.employeeNumber}</td>
                    <td style={getStyles().td}>{item.domesticWithoutStay}</td>
                    <td style={getStyles().td}>{item.domesticWithStay}</td>
                    <td style={getStyles().td}>{item.foreignWithStay}</td>
                    <td style={getStyles().td}>{item.domesticDailyAllowanceWithoutStay}</td>
                    <td style={getStyles().td}>{item.domesticDailyAllowanceWithStay}</td>
                    <td style={getStyles().td}>{item.foreignDailyAllowanceWithStay}</td>
                    <td style={getStyles().td}>
                      {item.domesticWithoutStay * item.domesticDailyAllowanceWithoutStay}
                    </td>
                    <td style={getStyles().td}>{item.domesticWithStay * item.domesticDailyAllowanceWithStay}</td>
                    <td style={getStyles().td}>{item.foreignWithStay * item.foreignDailyAllowanceWithStay}</td>
                    <td style={getStyles().td}>
                      {item.domesticWithoutStay * item.domesticDailyAllowanceWithoutStay +
                        item.domesticWithStay * item.domesticDailyAllowanceWithStay +
                        item.foreignWithStay * item.foreignDailyAllowanceWithStay}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </OrganizationBody>
    </div>
  );
};

function getStyles() {
  return {
    filterContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      alignItems: 'flex-start',
      padding: '10px 0 0'
    } as React.CSSProperties,
    dateGroup: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '5px'
    } as React.CSSProperties,
    labelText: {
      margin: 0,
      paddingTop: '3px'
    } as React.CSSProperties,
    searchSection: {
      padding: '10px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      backgroundColor: '#f9f9f9'
    } as React.CSSProperties,
    searchItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    } as React.CSSProperties,
    table: {
      margin: 0,
      fontSize: '12px',
      width: 'auto'
    } as React.CSSProperties,
    th: {
      padding: '8px'
    } as React.CSSProperties,
    td: {
      padding: '8px'
    } as React.CSSProperties,
    button: {
      marginLeft: '5px',
      padding: '3px'
    } as React.CSSProperties,
    select: {
      marginLeft: '10px',
      marginRight: '5px',
      height: '25px'
    } as React.CSSProperties,
    label: {
      paddingLeft: '16px',
      margin: 0
    } as React.CSSProperties
  } as const;
}

export default Allowances;
